import {
  useState,
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  FC,
  PropsWithChildren,
} from "react";
import { Algo, AlgoConfiguration } from "../types";
import { ListAlgosResponse, parseUptime, Uptime } from "../lazerApi";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useSelectedAreaEicCodes } from "../atoms/selectedGrid";
import { ALL_GRIDS } from "../datagrids/shared/constants";
import { inFloatingLayer } from "../../../floatingLayer";
import { toAreaId } from "../../orderBook/allGrids";

interface ListedAlgoContextType {
  selectedAlgos: Algo<AlgoConfiguration>[];
  addPreventClearAlgosRef: (ref: React.RefObject<HTMLElement | null>) => void;
  removePreventClearAlgosRef: (
    ref: React.RefObject<HTMLElement | null>
  ) => void;
  setSelectedAlgos: (algos: Algo<AlgoConfiguration>[]) => void;
  excludedRefs: React.RefObject<HTMLElement | null>[];
}

const defaultContextValue: ListedAlgoContextType = {
  selectedAlgos: [],
  addPreventClearAlgosRef: () => {},
  removePreventClearAlgosRef: () => {},
  setSelectedAlgos: () => {},
  excludedRefs: [],
};

const ListedAlgoContext =
  createContext<ListedAlgoContextType>(defaultContextValue);

export function useListedAlgo(): ListedAlgoContextType {
  return useContext(ListedAlgoContext);
}

export const ListedAlgoProvider: FC<PropsWithChildren> = ({ children }) => {
  const [selectedAlgos, setSelectedAlgos] = useState<Algo<AlgoConfiguration>[]>(
    []
  );

  // For easier debugging
  useEffect(() => {
    if (selectedAlgos.length > 0) {
      console.log("Selected Algo(s):", selectedAlgos);
    }
  }, [selectedAlgos]);

  const [excludedRefs, setExcludedRefs] = useState<
    React.RefObject<HTMLElement | null>[]
  >([]);

  const addPreventClearAlgosRef = useCallback(
    (ref: React.RefObject<HTMLElement | null>) => {
      setExcludedRefs((prev) => {
        if (!prev.includes(ref)) {
          return [...prev, ref];
        }
        return prev; // Avoid updating if the ref is already included
      });
    },
    [setExcludedRefs]
  );

  // The config popup mounts and unmounts per edit, so without this its stale
  // refs would pile up in `excludedRefs` for the life of the session.
  const removePreventClearAlgosRef = useCallback(
    (ref: React.RefObject<HTMLElement | null>) => {
      setExcludedRefs((prev) => prev.filter((existing) => existing !== ref));
    },
    [setExcludedRefs]
  );

  useClearAlgosWhenClickedOutside(setSelectedAlgos, excludedRefs);

  return (
    <ListedAlgoContext.Provider
      value={{
        selectedAlgos,
        setSelectedAlgos,
        addPreventClearAlgosRef,
        removePreventClearAlgosRef,
        excludedRefs,
      }}
    >
      {children}
    </ListedAlgoContext.Provider>
  );
};

const useClearAlgosWhenClickedOutside = (
  setSelectedAlgos: (algos: Algo<AlgoConfiguration>[]) => void,
  excludedRef: React.RefObject<HTMLElement | null>[]
) => {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Never clear while a selection modifier is held — the click is extending
      // a selection, not replacing it. Meta as well as ctrl, because ⌘ is the
      // modifier on macOS (where ctrl+click is a context-menu click), and shift
      // because that is how a range is picked.
      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }
      // A dropdown opened from an excluded panel is portalled onto `body`, so
      // it is not inside any of the refs — clicking an option would otherwise
      // clear the selection out from under the form.
      if (inFloatingLayer(event.target)) {
        return;
      }
      const index = excludedRef.findIndex((ref) =>
        isInsideRef(ref, event.target as Node)
      );
      if (index === -1) {
        setSelectedAlgos([]);
      }
    }

    document.addEventListener("mouseup", handleClickOutside);
    return () => {
      document.removeEventListener("mouseup", handleClickOutside);
    };
  }, [excludedRef, setSelectedAlgos]);
};

const isInsideRef = (
  ref: React.RefObject<HTMLElement | null>,
  target: Node
) => {
  return ref.current?.contains(target) ?? false;
};

/**
 * Every algo the hub knows about, narrowed server-side to the selected area.
 * The single source for the algos grid and — since the response carries them —
 * for the navbar's uptimes too.
 */
export const useAllAlgosData = () => {
  const [uptimes, setUptimes] = useState<Uptime[]>([]);
  const [eic] = useSelectedAreaEicCodes();

  const filteredEic = eic.filter((code) => code !== ALL_GRIDS);

  const params = filteredEic.length > 0 ? { eic: filteredEic } : undefined;

  const { data, isLoading, isPending } = useQuery<ListAlgosResponse>({
    queryKey: ["v1/algos", params],
    refetchInterval: 2000,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    setUptimes(data?.uptimes ? parseUptime(data.uptimes) : []);
  }, [data]);

  const algos = useMemo(() => data?.algos.map(withAreaId), [data]);

  return {
    algos,
    uptimes,
    loading: isLoading || isPending,
  };
};

const withAreaId = (
  algo: Algo<AlgoConfiguration>
): Algo<AlgoConfiguration> => ({
  ...algo,
  product_definitions: algo.product_definitions.map((pdef) => ({
    ...pdef,
    delivery_area_id: toAreaId(pdef.delivery_area_id),
  })),
});
